import { LicenseInfo } from '@mui/x-license';

/**
 * MUI X Pro license key, from `VITE_MUI_X_LICENSE_KEY` in the frontend `.env`.
 * Without one the Pro grid works but renders a watermark.
 */
const LICENSE_KEY: string = import.meta.env.VITE_MUI_X_LICENSE_KEY ?? '';

if (LICENSE_KEY) {
    LicenseInfo.setLicenseKey(LICENSE_KEY);
}

// Re-export the Pro grid (and its types) so the rest of the app imports the grid
// from here — guaranteeing the license above is applied first.
export * from '@mui/x-data-grid-pro';

import { createElement, useMemo, type RefAttributes } from 'react';
import { useTheme } from '@mui/material/styles';
import {
    DataGridPro as ProDataGrid,
    GridEditInputCell,
    type DataGridProProps,
    type GridColDef,
    type GridRenderEditCellParams,
    type GridComparatorFn,
    type GridLocaleText,
    type GridMultiSelectColDef,
    type GridValidRowModel
} from '@mui/x-data-grid-pro';
import dayjs from 'dayjs';
import { formatDate, formatDateTime, toDayString } from '../utils/formatDate';

import { compareNatural } from '../utils/compare';
import {
    useListFilterOperators,
    withListFilterOperators
} from './listFilterOperators';

export { useGrowingSelectOperators } from './listFilterOperators';

/** How a cell holding a list reads once copied or exported: "Bef. 12, Bef. 11". */
const LIST_SEPARATOR = ', ';

function joinList(value: unknown): unknown {
    return Array.isArray(value) ? value.join(LIST_SEPARATOR) : value;
}

/**
 * MUI joins a list with a bare ',' — the `multiSelect` type by its `separator`
 * default, any other column by `String(array)` — and that text is what Ctrl+C,
 * the CSV export and the quick search read. A column's own `separator` or
 * `valueFormatter` wins; the typed columns (number, date …) format themselves.
 */
export function withListSeparator<Row extends GridValidRowModel>(
    column: GridColDef<Row>
): GridColDef<Row> {
    // `type` alone does not narrow the union: the base column's is any string.
    if (column.type === 'multiSelect') {
        return (column as GridMultiSelectColDef<Row>).separator === undefined
            ? { ...column, separator: LIST_SEPARATOR }
            : column;
    }
    if (
        (column.type === undefined || column.type === 'string') &&
        !column.valueFormatter
    ) {
        return { ...column, valueFormatter: joinList as never };
    }
    return column;
}

function immediateEditCell(params: GridRenderEditCellParams) {
    return createElement(GridEditInputCell, { ...params, debounceMs: 0 });
}

/**
 * In row editing MUI's text and number cells hold a keystroke back for 200 ms.
 * When another cell of the row lands in that window, the grid re-runs this
 * cell's validation with its last handed-on value, and the cell resets its
 * text to it: what was just typed is gone ("200", Tab, "2" quickly drops the
 * 2). Handing every keystroke on at once leaves no such window. Columns with
 * their own edit cell pass `debounceMs` themselves (ValidatedGridEditCell
 * does in row editing).
 */
export function withImmediateRowEdits<Row extends GridValidRowModel>(
    column: GridColDef<Row>
): GridColDef<Row> {
    if (
        !column.editable ||
        column.renderEditCell ||
        (column.type !== undefined &&
            column.type !== 'string' &&
            column.type !== 'number')
    ) {
        return column;
    }
    return { ...column, renderEditCell: immediateEditCell };
}

/**
 * The Pro grid every screen renders — shadowing the re-export above, so that
 * {@link withListSeparator}, the typed list filters and, in row editing,
 * {@link withImmediateRowEdits} apply to all grids without each one opting in.
 */
export function DataGridPro<Row extends GridValidRowModel>(
    props: DataGridProProps<Row> & RefAttributes<HTMLDivElement>
) {
    const { columns, editMode } = props;
    const listOperators = useListFilterOperators();
    const separatedColumns = useMemo(
        () =>
            columns.map((column) => {
                const listed = withListFilterOperators(
                    withListSeparator(column),
                    listOperators
                );
                return editMode === 'row'
                    ? withImmediateRowEdits(listed)
                    : listed;
            }),
        [columns, listOperators, editMode]
    );
    return createElement(ProDataGrid<Row>, {
        ...props,
        columns: separatedColumns
    });
}

/**
 * Natural, numeric-aware sort for string columns whose values contain numbers
 * (record numbers, file names): "Bl 2" sorts before "Bl 10", not after. Use as a
 * column's `sortComparator` — the default string comparator sorts lexically.
 */
export const naturalSortComparator: GridComparatorFn = (
    firstValue,
    secondValue
) => compareNatural(String(firstValue ?? ''), String(secondValue ?? ''));

/**
 * Sorts a column whose value is a LIST (a Fund linked to Befund 12 and 7).
 * Each list is put in order first — naturally, so 2 before 10 — and then
 * compared entry by entry: the cell keeps showing the entries as they were
 * entered, but "12, 7" sorts with the 7s, and [7] before [7, 12]. A row
 * without any entry sorts like an empty cell, first.
 */
export const listSortComparator: GridComparatorFn = (
    firstValue,
    secondValue
) => {
    const inOrder = (value: unknown) =>
        (Array.isArray(value) ? value : [])
            .map((entry) => String(entry ?? ''))
            .sort(compareNatural);
    const firstList = inOrder(firstValue);
    const secondList = inOrder(secondValue);
    const length = Math.min(firstList.length, secondList.length);
    for (let i = 0; i < length; i++) {
        const order = compareNatural(firstList[i], secondList[i]);
        if (order !== 0) {
            return order;
        }
    }
    return firstList.length - secondList.length;
};

/** The same for a list shown as one ", "-joined text ("Planum, Profil"). */
export const joinedListSortComparator: GridComparatorFn = (
    firstValue,
    secondValue,
    firstParams,
    secondParams
) => {
    const split = (value: unknown) =>
        String(value ?? '')
            .split(', ')
            .filter(Boolean);
    return listSortComparator(
        split(firstValue),
        split(secondValue),
        firstParams,
        secondParams
    );
};

/**
 * The same for a list of IDS, compared by what the cell shows for them — a
 * Befund reference by "Bef. 7000, Fl. 1, Pl. 1", not by its uuid.
 */
export function labelledListSortComparator(
    labelOf: (id: string) => string
): GridComparatorFn {
    return (firstValue, secondValue, firstParams, secondParams) => {
        const labels = (value: unknown) =>
            (Array.isArray(value) ? value : []).map((id) =>
                labelOf(String(id))
            );
        return listSortComparator(
            labels(firstValue),
            labels(secondValue),
            firstParams,
            secondParams
        );
    };
}

/**
 * A stored record number ('5000') as a number. Free text ('12a', '') → null:
 * partial parsing would make a filter silently disagree with the value it shows.
 */
function toRecordNumber(value: unknown): number | null {
    if (typeof value === 'number') {
        return Number.isFinite(value) ? value : null;
    }
    const raw = String(value ?? '').trim();
    if (!/^\d+$/.test(raw)) {
        return null;
    }
    const parsed = Number(raw);
    return Number.isSafeInteger(parsed) ? parsed : null;
}

/**
 * A stored measure ('45', '1,20', '98.45') as a number. Anything else → null,
 * for the same reason as {@link toRecordNumber}.
 */
function toMeasure(value: unknown): number | null {
    if (typeof value === 'number') {
        return Number.isFinite(value) ? value : null;
    }
    const raw = String(value ?? '').trim();
    if (!/^-?\d+(?:[.,]\d+)?$/.test(raw)) {
        return null;
    }
    return Number(raw.replace(',', '.'));
}

/**
 * A column STORED AS A STRING but filtered and sorted as a number: `parse`
 * turns the value (after the caller's own `valueGetter`) into the number, and
 * the cell shows the stored text wherever `parse` gave up.
 */
function parsedNumberColumn<Row extends GridValidRowModel>(
    column: GridColDef<Row>,
    parse: (value: unknown) => number | null,
    format: (value: number, raw: unknown) => string
): GridColDef<Row> {
    // `GridValueGetter` pins the value type per column (default `never`), so the
    // caller's getter is re-typed loosely — only the shape of the call matters.
    type RawGetter = (
        value: unknown,
        row: Row,
        col: unknown,
        apiRef: unknown
    ) => unknown;
    const rawValue = (column.valueGetter ??
        ((value: unknown) => value)) as unknown as RawGetter;
    return {
        ...column,
        type: 'number',
        valueGetter: (value, row, col, apiRef) =>
            parse(rawValue(value, row, col, apiRef)),
        valueFormatter:
            column.valueFormatter ??
            ((value, row, col, apiRef) => {
                const raw = rawValue(
                    (row as Record<string, unknown>)[col.field],
                    row,
                    col,
                    apiRef
                );
                // Not a number: show the stored text rather than an empty cell.
                if (value === null) {
                    return String(raw ?? '');
                }
                return format(value as number, raw);
            }),
        // Read left-aligned like the text columns around them, not
        // right-aligned as `number` defaults.
        align: column.align ?? 'left',
        headerAlign: column.headerAlign ?? 'left'
    };
}

/**
 * Make a column whose value is STORED AS A STRING (Befundnummer, Planumnummer …)
 * behave as a real MUI X `number` column — the sibling of {@link dateColumn}.
 * A plain string column only offers the text operators ("contains 100" also
 * matches 1002); the parsed number buys numeric operators and sorting, while the
 * ROW keeps its stored string. A caller's own `valueGetter` runs first.
 *
 * A free-text number ('12a') must stay READABLE, so the formatter falls back to
 * the raw text. Only the filter treats it as empty.
 */
export function recordNumberColumn<Row extends GridValidRowModel>(
    column: GridColDef<Row>
): GridColDef<Row> {
    // Plain digits: an identifier must never pick up the thousands separator a
    // quantity would ('5.000').
    return parsedNumberColumn(column, toRecordNumber, (value) => String(value));
}

/**
 * The same for a MEASURE (Tiefe, Breite, Höhe …): stored as typed, with a
 * decimal comma or point, sorted and filtered by its value: "9" before "45",
 * "0,85" before "0,9". The cell shows the text as typed, so "1,20"
 * keeps its trailing zero.
 */
export function measureColumn<Row extends GridValidRowModel>(
    column: GridColDef<Row>
): GridColDef<Row> {
    return parsedNumberColumn(column, toMeasure, (_value, raw) =>
        String(raw ?? '')
    );
}

function toDate(value: unknown): Date | null {
    if (!value) {
        return null;
    }
    const parsed = dayjs(value as string | number | Date);
    return parsed.isValid() ? parsed.toDate() : null;
}

/**
 * Make a column whose value is STORED AS A STRING (ISO datetime or 'YYYY-MM-DD')
 * behave as a real MUI X `date` column: the text filter ("contains") is useless
 * on a date. The cell still renders German DD.MM.YYYY and the ROW keeps its
 * stored string. A caller's own `valueFormatter` wins, but is handed the Date.
 *
 * Editable columns keep storing strings — that is the `valueSetter`'s job. An
 * edit cell is handed the parsed **Date** when edit mode OPENS and the
 * 'YYYY-MM-DD' string only once the picker has written one, so an untouched cell
 * would otherwise commit a `Date` into the save path. Validators comparing the
 * stored form still need `toDayString` on their own input.
 *
 * ⚠ The stored form is a DAY — a full ISO datetime (`recordedAt`) would lose its
 * time-of-day if made inline-editable, and needs its own `valueSetter`.
 */
export function dateColumn<Row extends GridValidRowModel>(
    column: GridColDef<Row>
): GridColDef<Row> {
    return {
        ...column,
        type: 'date',
        valueGetter: (value) => toDate(value),
        valueSetter:
            column.valueSetter ??
            ((value, row) => ({
                ...row,
                [column.field]: toDayString(value)
            })),
        valueFormatter:
            column.valueFormatter ?? ((value) => formatDate(value as Date))
    };
}

/**
 * The time-of-day sibling of {@link dateColumn}: a stored ISO datetime as a real
 * MUI X `dateTime` column, with German 'DD.MM.YYYY HH:mm:ss' in the cell. A
 * caller's own `valueGetter` runs first and its result is what gets parsed.
 *
 * Read-only on purpose — these are the server-written audit stamps, which no
 * cell may edit, hence no `valueSetter`.
 */
export function dateTimeColumn<Row extends GridValidRowModel>(
    column: GridColDef<Row>
): GridColDef<Row> {
    // Same loose re-typing as `recordNumberColumn`.
    type RawGetter = (
        value: unknown,
        row: Row,
        col: unknown,
        apiRef: unknown
    ) => unknown;
    const rawValue = (column.valueGetter ??
        ((value: unknown) => value)) as unknown as RawGetter;
    return {
        ...column,
        type: 'dateTime',
        editable: false,
        valueGetter: (value, row, col, apiRef) =>
            toDate(rawValue(value, row, col, apiRef)),
        valueFormatter:
            column.valueFormatter ?? ((value) => formatDateTime(value as Date))
    };
}

/**
 * A grid's own text overrides, ON TOP of the app's language rather than instead
 * of it: `localeText` is an ordinary prop, so a grid that passes one REPLACES the
 * German bundle the theme installs (see LocalizedThemeProvider) and falls back to
 * MUI's English everywhere it does not name. Pass only what a grid really owns:
 *
 * ```tsx
 * localeText={useGridLocaleText({ noRowsLabel: … })}
 * ```
 */
export function useGridLocaleText(
    overrides: Partial<GridLocaleText>
): Partial<GridLocaleText> {
    const theme = useTheme();
    const fromLanguage =
        theme.components?.MuiDataGrid?.defaultProps?.localeText;
    // Keyed on the CONTENT, not the identity: the overrides are written inline,
    // and a new `localeText` each render churns the grid's state. JSON.stringify
    // drops FUNCTIONS without a trace, so a caller overriding one would be served
    // its first render's closure for ever — a callback opts out of the memo.

    const overridesKey = JSON.stringify(overrides);
    const hasCallback = Object.values(overrides).some(
        (value) => typeof value === 'function'
    );
    return useMemo(
        () => ({ ...fromLanguage, ...overrides }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [fromLanguage, overridesKey, hasCallback ? overrides : null]
    );
}
